import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import "./styles.scss";
import AuthLayout from "Layouts/Auth";
import { notifySuccess } from "Services/Utils/toastNotification";
import InputBox from "Components/InputBox";
import Button from "Components/Button";
import ErrorMessage from "Components/ErrorMessage";
import Select from "react-select";
import { COUNTRIES } from "Services/Utils/validateInput";
import { contactUsRequest } from "../../../../Redux/Slices/Common/commonSlice";

const ContactUs = () => {
  // Initialize redux and navigation
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management
  const [formData, setFormData] = useState({
    company_name: "",
    country: "",
    industry: "",
    key_contact_name: "",
    email: "",
    phoneNumber: "",
    countryCode: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const alphaOnly = (value) => /^[A-Za-z\s]+$/.test(value);
  const notNumericOnly = (value) => !/^\d+$/.test(value); 
  const numericOnly = (value) => /^\d*$/.test(value);

  const MAX_LENGTHS = {
    company_name: 50, 
    key_contact_name: 50, 
    industry: 50, 
    country: 50, 
    email: 100, 
  };

  const COUNTRY_CODES = COUNTRIES.map((country) => ({
    value: country.country_code,
    label: `${country.country_name} (${country.country_code})`,
    flag: country.country_flag,
    digitLength: country.digit_length,
  })).sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    const india = COUNTRIES.find(
      (country) => country.country_name === "India"
    );

    if (india) {
      setFormData((prev) => ({
        ...prev,
        countryCode: india.country_code,
      }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone number to allow only numbers
    if (name === "phoneNumber") {
      const numericValue = value.replace(/\D/g, "");

      // Get the selected country's digit length
      const selectedCountry = COUNTRIES.find(
        (c) => c.country_code === formData.countryCode
      );
      const maxLength = selectedCountry?.digit_length || 10;

      const limitedValue = numericValue.slice(0, maxLength);

      setFormData((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Company name validation
    if (!formData.company_name) {
      newErrors.company_name = "Company name is required";
    } else if (!notNumericOnly(formData.company_name)) {
      newErrors.company_name = "Company name cannot be numeric only";
    } else if (formData.company_name.length < 2) {
      newErrors.company_name =
        "Company name must be at least 2 characters long";
    }

    // Country validation
    if (!formData.country) {
      newErrors.country = "Country is required";
    } else if (!alphaOnly(formData.country)) {
      newErrors.country = "Country name must contain only letters";
    }

    // Industry validation
    if (!formData.industry) {
      newErrors.industry = "Industry is required";
    } else if (!notNumericOnly(formData.industry)) {
      newErrors.industry = "Industry cannot be numeric only";
    }

    // Key contact name validation
    if (!formData.key_contact_name) {
      newErrors.key_contact_name = "Contact name is required";
    } else if (!alphaOnly(formData.key_contact_name)) {
      newErrors.key_contact_name = "Contact name must contain only letters";
    } else if (formData.key_contact_name.length < 2) {
      newErrors.key_contact_name =
        "Contact name must be at least 2 characters long";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    // Phone number validation
    if (!formData.countryCode) {
      newErrors.countryCode = "Country code is required";
    }
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      const selectedCountry = COUNTRIES.find(
        (c) => c.country_code === formData.countryCode
      );
      if (
        selectedCountry &&
        formData.phoneNumber.length !== selectedCountry.digit_length
      ) {
        newErrors.phoneNumber = `Phone number must be exactly ${selectedCountry.digit_length} digits`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const onSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) return;

    setIsSubmitting(true);
    dispatch(
      contactUsRequest({
        data: {
          company_name: formData.company_name,
          country: formData.country,
          industry: formData.industry,
          key_contact_name: formData.key_contact_name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          country_code: formData.countryCode,
        },
        callBack: contactUsRequestCB,
      })
    );
  };

  const contactUsRequestCB = (data) => {
    setIsSubmitting(false);

    if (data.status === 400 || data.staus === 404) {
      // Handle error case
      return;
    }

    if (data.status === 200) {
      notifySuccess("Your contact request has been submitted successfully!");

      // Reset form
      setFormData({
        company_name: "",
        country: "",
        industry: "",
        key_contact_name: "",
        email: "",
        phoneNumber: "",
        countryCode: "",
      });

      // Navigate to login or home page
      navigate("/");
    }
  };

  // Styling
  const input_box = {
    width: "85%",
    paddingRight: "2.5rem",
    fontSize: "16px",
  };
  const phoneInputStyles = {
    container: {
      display: "flex",
      width: "100%",
      border: "1px solid #464646",
      borderRadius: "7px",
      minHeight: "64px",
      alignItems: "center",
    },
    countryCodeSelect: {
      minHeight: "48px",
      width: "150px",
      border: "none",
      borderRight: "1px solid #B2BEC3",
      backgroundColor: "white",
    },
    numberInput: {
      flex: 1,
      border: "none",
      padding: "0 15px",
      color: "#697077",
      fontSize: "16px",
      outline: "none",
      minHeight: "48px",
    },
  };

  return (
    <AuthLayout>
      <div className="contact-us-root-container">
        <h1 className="contact-us-heading">Contact Us</h1>
        {/* <p className="contact-us-sub-heading">
          Please fill out the form below and we'll get back to you
        </p>
        <hr className="contact-us-hr" />
         */}
        <form onSubmit={onSubmit}>
          <InputBox
            label="Company Name"
            name="company_name"
            value={formData.company_name}
            onChange={handleInputChange}
            inputStyle={input_box}
            maxLength={MAX_LENGTHS.company_name}
          />
          <ErrorMessage message={errors.company_name} />
          <div className="contact-us-spacer" />

          <InputBox
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            inputStyle={input_box}
            maxLength={MAX_LENGTHS.country}
          />
          <ErrorMessage message={errors.country} />
          <div className="contact-us-spacer" />

          <InputBox
            label="Industry"
            name="industry"
            value={formData.industry}
            onChange={handleInputChange}
            inputStyle={input_box}
            maxLength={MAX_LENGTHS.industry}
          />
          <ErrorMessage message={errors.industry} />
          <div className="contact-us-spacer" />

          <InputBox
            label="Key Contact Name"
            name="key_contact_name"
            value={formData.key_contact_name}
            onChange={handleInputChange}
            inputStyle={input_box}
            maxLength={MAX_LENGTHS.key_contact_name}
          />
          <ErrorMessage message={errors.key_contact_name} />
          <div className="contact-us-spacer" />

          <InputBox
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            inputStyle={input_box}
            maxLength={MAX_LENGTHS.email}
          />
          <ErrorMessage message={errors.email} />
          <div className="contact-us-spacer" />

          <div style={{ width: "85%" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontFamily: "Open Sans",
                fontSize: "1rem",
                color: "#697077",
              }}
            >
              Phone Number
            </label>
            <div style={phoneInputStyles.container}>
              <Select
                options={COUNTRY_CODES}
                value={COUNTRY_CODES.find(
                  (option) => option.value === formData.countryCode
                )}
                onChange={(selectedOption) =>
                  setFormData((prev) => ({
                    ...prev,
                    countryCode: selectedOption ? selectedOption.value : "",
                    phoneNumber: "",
                  }))
                }
                placeholder="Code"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    width: "150px",
                    minHeight: "48px",
                    border: "none",
                    borderRight: "1px solid #B2BEC3",
                    borderRadius: "4px 0 0 4px",
                    boxShadow: "none",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    width: "250px", // Add this for dropdown width
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: "0 8px",
                  }),
                  indicatorSeparator: () => ({ display: "none" }), // Add this
                }}
              />
              <input
                style={phoneInputStyles.numberInput}
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter phone number"
              />
            </div>
            <ErrorMessage message={errors.phoneNumber} />
          </div>

          <div className="contact-us-back-container">
            <div onClick={() => navigate("/")}>
              <p className="contact-us-back-text">Back to Login</p>
            </div>
          </div>

          <Button
            title="Submit"
            buttonStyle={{ width: "85%" }}
            disabled={isSubmitting}
            forForm
          />
        </form>
      </div>
    </AuthLayout>
  );
};

export default ContactUs;